import React from "react";
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import SectionIcon from "@mui/icons-material/HorizontalSplit";
import { pageScrollStyle } from "../../../../../styles/styles";
import { DeleteRounded } from "@mui/icons-material";
import { fieldTypeLabelMap } from "../../../../../static/staticdata";

const Sidebar = ({
  tabBtn,
  customFieldsExpanded,
  unUsedExpanded,
  usedExpanded,
  toggleCustomFields,
  toggleUnusedFields,
  toggleUsedFields,
  allCustomFieldsTypes,
  unUsedFieldsTypes,
  usedFieldsTypes,
  handleDragStart,
  handleDragEnd,
  handleSectionDragStart,
  setUnUsedFieldsTypes,
  setUsedFieldsTypes,
  templates = [],
  copyTemplateId,
  setCopyTemplateId,
  onCopyTemplate,
  isEditMode = false,
}) => {
  const handleRemoveUnUsedField = (fieldId) => {
    if (typeof setUnUsedFieldsTypes === "function") {
      setUnUsedFieldsTypes((prev) =>
        prev.filter((field) => field.id !== fieldId)
      );
      setUsedFieldsTypes((prev) =>
        prev.filter((field) => field.id !== fieldId)
      );
    }
  };

  const handleUsedFieldDragStart = (e, item) => {
    if (!item.disabled) {
      handleDragStart(e, item);
    }
  };

  return (
    <Box
      sx={{
        width: "25%",
        position: "relative",
        borderRight: "1px solid #e0e0e0",
        backgroundColor: "#ffffff",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {tabBtn === 1 ? (
        <>
          {!isEditMode && (
            <Box sx={{ p: "0.5rem" }}>
              <FormControl
                label="Get field from other template"
                size="small"
                fullWidth
              >
                <Select
                  displayEmpty
                  value={copyTemplateId || ""}
                  onChange={(e) => {
                    setCopyTemplateId?.(e?.target.value);
                    if (onCopyTemplate) onCopyTemplate(e?.target?.value);
                  }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {Array?.isArray(templates) ? (
                    templates.map((t) => (
                      <MenuItem key={t?.id} value={t?.id}>
                        {t?.template_name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No templates available</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>
          )}
          {/* Add new section button */}
          <Box sx={{ p: "0.5rem" }}>
            <Box
              id="drag_section"
              draggable="true"
              onDragStart={handleSectionDragStart}
              onDragEnd={handleDragEnd}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                p: "0.5rem",
                display: "flex",
                gap: "0.5rem",
                alignItems: "center",
                justifyContent: "center",
                cursor: "grab",
                bgcolor: "#f0f7ff",
                "&:hover": {
                  bgcolor: "#e1f5fe",
                },
              }}
            >
              <SectionIcon sx={{ color: "primary.main" }} />
              <Typography variant="body2" sx={{ fontWeight: "500" }}>
                Drag to Add New Section
              </Typography>
            </Box>
          </Box>

          {/* Custom Fields */}
          <Box>
            <Box
              id="custom_field_picker"
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
              }}
            >
              <Box
                onClick={toggleCustomFields}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: "0.5rem",
                  cursor: "pointer",
                  borderBottom: " 1px solid #e0e0e0",
                  bgcolor: "#f7f9fc",
                }}
              >
                <Typography
                  variant="body1"
                  color="black"
                  sx={{
                    fontSize: "1rem",
                    fontWeight: customFieldsExpanded ? "600" : "500",
                  }}
                >
                  Custom Fields
                </Typography>
                {customFieldsExpanded ? (
                  <ExpandLessRoundedIcon />
                ) : (
                  <ExpandMoreRoundedIcon />
                )}
              </Box>

              <Collapse in={customFieldsExpanded}>
                <Box
                  sx={{
                    display: "flex",
                    gap: "0.5rem",
                    flexWrap: "wrap",
                    overflowY: "auto",
                    p: "0.5rem",
                    height: "50vh",
                    ...pageScrollStyle,
                  }}
                >
                  {allCustomFieldsTypes.map((item, i) => (
                    <Box
                      key={i}
                      draggable="true"
                      onDragStart={(e) => handleDragStart(e, item)}
                      onDragEnd={handleDragEnd}
                      sx={{
                        fontSize: "0.8rem",
                        border: "1px solid #e0e0e0",
                        borderRadius: "4px",
                        py: "0.4rem",
                        px: "0.6rem",
                        whiteSpace: "nowrap",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.4rem",
                        cursor: "grab",
                        backgroundColor: "#ffffff",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                          borderColor: "#bbbbbb",
                        },
                        mb: "0.5rem",
                        width: "calc(50% - 0.5rem)",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          color: "primary.main",
                        }}
                      >
                        {item.icons}
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "0.75rem", fontWeight: "500" }}
                      >
                        {item.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Box>
          </Box>

          {/* Unused Fields */}
          <Box>
            <Box
              id="Un_used_field_picker"
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
              }}
            >
              <Box
                onClick={toggleUnusedFields}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: "0.5rem",
                  cursor: "pointer",
                  borderBottom: " 1px solid #e0e0e0",
                  bgcolor: "#f7f9fc",
                }}
              >
                <Typography
                  variant="body1"
                  color="black"
                  sx={{
                    fontSize: "1rem",
                    fontWeight: unUsedExpanded ? "600" : "500",
                  }}
                >
                  Unused Fields
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: "1rem",
                    alignItems: "center",
                  }}
                >
                  {unUsedFieldsTypes.length > 0 && (
                    <Typography
                      variant="body1"
                      sx={{
                        height: "1.5rem",
                        width: "1.5rem",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        fontSize: "0.9rem",
                        fontWeight: "500",
                        bgcolor: "primary.main",
                        color: "white.main",
                        borderRadius: "50%",
                      }}
                    >
                      {unUsedFieldsTypes.length}
                    </Typography>
                  )}
                  {unUsedExpanded ? (
                    <ExpandLessRoundedIcon />
                  ) : (
                    <ExpandMoreRoundedIcon />
                  )}
                </Box>
              </Box>

              <Collapse in={unUsedExpanded}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                    overflowY: "auto",
                    p: "0.5rem",
                    height: "50vh",
                    ...pageScrollStyle,
                  }}
                >
                  {unUsedFieldsTypes.map((item, i) => (
                    <Box
                      key={i}
                      draggable="true"
                      onDragStart={(e) => handleDragStart(e, item)}
                      onDragEnd={handleDragEnd}
                      sx={{
                        fontSize: "0.8rem",
                        border: "1px solid #e0e0e0",
                        borderRadius: "4px",
                        py: "0.2rem",
                        px: "0.6rem",
                        whiteSpace: "nowrap",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "0.4rem",
                        cursor: "grab",
                        backgroundColor: "#ffffff",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                          borderColor: "#bbbbbb",
                        },
                        mb: "0.5rem",
                      }}
                    >
                      <Box sx={{ display: "flex", gap: "0.5rem" }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            color: "primary.main",
                          }}
                        >
                          {item.icons}
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem", fontWeight: "500" }}
                        >
                          {item.label}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "0.75rem", fontWeight: "500" }}
                      >
                        {fieldTypeLabelMap[item?.value] || "Unknown Field Type"}
                      </Typography>
                      <Tooltip title="Delete permanently">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleRemoveUnUsedField(item.id)}
                        >
                          <DeleteRounded sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Box>
          </Box>
        </>
      ) : (
        <Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
            }}
          >
            <Box
              onClick={toggleUsedFields}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: "0.5rem",
                cursor: "pointer",
                borderBottom: " 1px solid #e0e0e0",
                bgcolor: "#f7f9fc",
              }}
            >
              <Typography
                variant="body1"
                color="black"
                sx={{
                  fontSize: "1rem",
                  fontWeight: usedExpanded ? "600" : "500",
                }}
              >
                Used Fields
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: "1rem",
                  alignItems: "center",
                }}
              >
                {usedFieldsTypes.length > 0 && (
                  <Typography
                    variant="body1"
                    sx={{
                      height: "1.5rem",
                      width: "1.5rem",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      fontSize: "0.9rem",
                      fontWeight: "500",
                      bgcolor: "primary.main",
                      color: "white.main",
                      borderRadius: "50%",
                    }}
                  >
                    {usedFieldsTypes.length}
                  </Typography>
                )}
                {usedExpanded ? (
                  <ExpandLessRoundedIcon />
                ) : (
                  <ExpandMoreRoundedIcon />
                )}
              </Box>
            </Box>

            <Collapse in={usedExpanded}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.5rem",
                  overflowY: "auto",
                  p: "0.5rem",
                  height: "calc(100vh - 3rem)",
                  ...pageScrollStyle,
                }}
              >
                {usedFieldsTypes.length === 0 ? (
                  <Box
                    sx={{
                      width: "100%",
                      textAlign: "center",
                      color: "#757575",
                      fontSize: "0.8rem",
                      fontStyle: "italic",
                      py: "1rem",
                    }}
                  >
                    No unused fields available
                  </Box>
                ) : (
                  usedFieldsTypes?.map((item, i) => (
                    <Box
                      key={item.id}
                      draggable={!item.disabled}
                      onDragStart={(e) => handleUsedFieldDragStart(e, item)}
                      onDragEnd={handleDragEnd}
                      sx={{
                        p: "0.5rem",
                        borderRadius: "0.25rem",
                        backgroundColor: item.disabled ? "#e0e0e0" : "#ffffff",
                        cursor: item.disabled ? "not-allowed" : "grab",
                        opacity: item.disabled ? 0.7 : 1,
                        border: "1px solid #e0e0e0",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "1rem",
                        "&:hover": {
                          backgroundColor: item.disabled
                            ? "#e0e0e0"
                            : "#f5f5f5",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "1rem",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            color: "primary.main",
                          }}
                        >
                          {item.icons}
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem", fontWeight: "500" }}
                        >
                          {item.label}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "0.75rem", fontWeight: "500" }}
                      >
                        {fieldTypeLabelMap[item?.value] || "Unknown Field Type"}
                      </Typography>
                    </Box>
                  ))
                )}
              </Box>
            </Collapse>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Sidebar;
